import React, { useCallback, useState, useEffect } from 'react';
import { Input, message, Tag } from 'antd';
import Icon from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import styles from './TextCreationAssistant.module.css';
import { ReactComponent as JsonFileIcon } from './gates/script/ScriptCodingAssistant/json-file.svg';
import { ReactComponent as TextFileIcon } from './gates/script/ScriptCodingAssistant/text-fle.svg';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import platformApi from '@src/api/platform/platformApi';
import { useTranslation } from '@hooks/index';
import RoundedModal from '@components/roundedModal/roundedModal';
import { SUBSCRIPTION_LIMIT_REACHED } from '@common/constants';
import { decodeServerErrorInfo } from '@src/app/reducers/widget/utils';
import { showGlobalNotification, showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';

/**
 * Removes markdown code block wrappers from a string.
 * @param text - The string with potential markdown wrappers.
 * @returns The text string without markdown wrappers.
 */
const removeMarkdownWrappers = (text: string): string => {
  // Remove starting ```optional_language and closing ```
  const regex = /^```(?:[a-zA-Z0-9]+)?\n?([\s\S]*?)\n?```$/;
  const match = text.match(regex);
  if (match && match[1]) {
    return match[1].trim();
  }
  // If no match, or if the capturing group is empty, return the original string trimmed
  return text.trim();
};

type Props = {
  show: boolean;
  currentText?: string;
  jsonData?: string;
  onTextGenerated: (text: string) => void;
  onCancel: () => void;
}

type ClipboardPreview = {
  type: 'text' | 'json';
  content: string;
}

type ActiveContextItem = {
  content: string;
  type: 'text' | 'json';
}

type ActiveContext = {
  text?: ActiveContextItem;
  json?: ActiveContextItem;
  clipboard?: ActiveContextItem;
}

/**
 * Modal component for generating text using AI assistance
 * @param show - Whether the modal is visible
 * @param currentText - Optional current text to be used as context
 * @param jsonData - Optional JSON data for context
 * @param onTextGenerated - Callback when text is generated
 * @param onCancel - Callback when modal is cancelled
 */
const TextCreationAssistant = (props: Props) => {
  const { show, currentText: propCurrentText, jsonData: propJsonData, onTextGenerated, onCancel } = props;
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const currentUser = useSelector(selectSignedUserProfile);
  const dispatch = useDispatch();
  const t = useTranslation('LogicMapper.TextCreationAssistant');
  const [activeContext, setActiveContext] = useState<ActiveContext>({});

  const handleRemoveContext = (type: 'text' | 'json' | 'clipboard') => {
    setActiveContext(prev => {
      const newContext = { ...prev };
      if (type === 'text') { delete newContext.text; }
      if (type === 'json') { delete newContext.json; }
      if (type === 'clipboard') { delete newContext.clipboard; }
      return newContext;
    });
  };

  /**
   * Handles the text generation request
   */
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      message.warning(t('EmptyPromptWarning'));
      return;
    }

    if (!currentUser.profile?.id) {
      message.error(t('MissingApiKey'));
      return;
    }

    setIsGenerating(true);
    let accumulatedResponse = '';

    try {
      await platformApi.textGen({
        prompt,
        existingText: activeContext.text?.content,
        jsonData: activeContext.json?.content,
        userId: currentUser.profile.id,
        onChunk: (chunk) => {
          accumulatedResponse += chunk;
        },
      });

      const cleanedText = removeMarkdownWrappers(accumulatedResponse);
      onTextGenerated(cleanedText);

      // Close the modal after successful generation
      onCancel();
      // Reset prompt, but keep tag states as they are unless props change
      setPrompt('');

    } catch (error) {
      console.error('Error generating text:', error);
      // message.error(t('GenerationError'));
      const { errorCode, message: errMessage } = decodeServerErrorInfo(error);

      if(errorCode === SUBSCRIPTION_LIMIT_REACHED){
        dispatch(showPremiumUpgradeModal(true));
      }
      
      dispatch(showGlobalNotification({
        type: 'error',
        code: 'script-creation',
        title: t('GenerationError'),
        message: errMessage,
      }));

    } finally {
      setIsGenerating(false);
    }
  }, [prompt, t, onTextGenerated, onCancel, activeContext]);

  /**
   * Handles modal cancellation
   */
  const handleCancel = useCallback(() => {
    if (!isGenerating) {
      setPrompt('');
      // Active context will be reset by useEffect when props change (modal reopens)
      onCancel();
    }
  }, [isGenerating, onCancel]);

  /**
   * Handles Enter key press in the textarea
   */
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  }, [handleGenerate, isGenerating]);



  // Initialize active context from props when component mounts or props change
  useEffect(() => {
    const initialContext: ActiveContext = {};

    if (propCurrentText) {
      initialContext.text = { content: propCurrentText, type: 'text' };
    }

    if (propJsonData) {
      initialContext.json = { content: propJsonData, type: 'json' };
    }

    setActiveContext(initialContext);
  }, [propCurrentText, propJsonData, show]); // Re-init when modal is shown

  return (
    <RoundedModal
      title={t('Title')}
      visible={show}
      centered
      onCancel={handleCancel}
      footerClassName={styles.Footer}
      okBtnLabel={t('GenerateBtn')}
      cancelBtnLabel={t('CancelBtn')}
      onOk={handleGenerate}
      loadingOkBtn={isGenerating}
      disableOkButton={!prompt.trim() || isGenerating}
      disableCancelButton={isGenerating}
      closeOnMaskClick={false}
      closable={!isGenerating}
    >
      <div className={styles.Content}>
        <div className={styles.Description}>
          {t('Description')}
        </div>

        <Input.TextArea
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t('PromptPlaceholder')}
          disabled={isGenerating}
          onKeyDown={handleKeyPress}
          className={styles.PromptInput}
        />
        <div className={styles.AttachmentsContainer}>
          {activeContext.text && (
            <Tag
              closable
              onClose={() => handleRemoveContext('text')}
              icon={<Icon component={TextFileIcon} />}
              className={styles.ContextTag}
            >
              {t('ContextExistingTextTag')}
            </Tag>
          )}

          {activeContext.json && (
            <Tag
              closable
              onClose={() => handleRemoveContext('json')}
              icon={<Icon component={JsonFileIcon} />}
              className={styles.ContextTag}
            >
              {t('ContextJsonDataTag')}
            </Tag>
          )}
        </div>
      </div>
    </RoundedModal>
  );
};

export default TextCreationAssistant;
